import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { get } from '@/features/shared/lib/api';

/**
 * DepartmentOutput5YearChart
 * - Multi-line chart for 5-year resources output (SCI, Scopus, KCI)
 * - Props:
 *   - series: Array<{ key: string, label: string, color: string, values: Array<{ x: string|number, y: number }> }>
 *   - xLabels: string[]|number[] (optional; derived from series[0].values if omitted)
 *   - height: number (optional; default 220)
 *   - departmentCode: department code for API call
 */
export default function DepartmentOutput5YearChart({ series: externalSeries = null, xLabels, height = 220, departmentCode = null }) {
  // API에서 데이터 가져오기 (externalSeries가 없을 때만)
  const [outputData, setOutputData] = useState(null);
  const [outputLoading, setOutputLoading] = useState(false);
  const [outputErr, setOutputErr] = useState(null);

  useEffect(() => {
    // externalSeries가 제공되면 API 호출하지 않음
    if (externalSeries !== null) {
      return;
    }

    // departmentCode가 없으면 API 호출하지 않음
    if (!departmentCode) {
      return;
    }

    let alive = true;
    async function fetchOutput() {
      setOutputLoading(true);
      setOutputErr(null);
      try {
        const response = await get(`/rm/v1/department/${encodeURIComponent(departmentCode)}/output_5year`);
        if (alive) {
          if (response?.status && response?.result) {
            setOutputData(response.result);
          } else {
            setOutputErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setOutputData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setOutputErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setOutputLoading(false);
        }
      }
    }
    fetchOutput();
    return () => { alive = false; };
  }, [externalSeries, departmentCode]);

  // 데이터 변환: API 데이터를 series 형식으로 변환
  const series = useMemo(() => {
    if (Array.isArray(externalSeries) && externalSeries.length) {
      return externalSeries;
    }

    if (outputData && typeof outputData === 'object') {
      const mapSeries = (key, label, color, arr) => {
        const rows = Array.isArray(arr) ? arr : [];
        const values = rows
          .filter(d => d && typeof d.indicator_year === 'number' && d.indicator_year > 0 && typeof d.impact !== 'undefined' && d.impact !== null && !isNaN(Number(d.impact)))
          .map(d => ({ x: d.indicator_year, y: Number(d.impact) }))
          .sort((a, b) => a.x - b.x);
        if (!values.length) return null;
        return { key, label, color, values };
      };

      const sci = mapSeries('SCI', 'SCIs', '#B298F2', outputData.SCI);
      const scopus = mapSeries('Scopus', 'Scopus', '#FFBE8D', outputData.Scopus);
      const kci = mapSeries('KCI', 'KCI', '#5FA3E0', outputData.KCI);
      const result = [sci, scopus, kci].filter(Boolean);
      if (result.length > 0) return result;
    }

    return null;
  }, [externalSeries, outputData]);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // 로딩 중이면 차트를 그리지 않음
    if (externalSeries === null && outputLoading) {
      el.innerHTML = '';
      return;
    }

    const draw = () => {
      el.innerHTML = '';

      // API 모드인데 데이터가 없으면 안내 문구만 표시
      if (externalSeries === null && !outputLoading && (!Array.isArray(series) || series.length === 0)) {
        const msg = document.createElement('div');
        msg.setAttribute('class', 'flex items-center justify-center min-h-[220px] text-[#8D9AAF] text-sm');
        msg.textContent = '데이터가 없습니다.';
        el.appendChild(msg);
        return;
      }

      // Fallback sample (matches the provided design) — externalSeries가 있을 때만 사용
      const src = Array.isArray(series) && series.length ? series : fallbackSeries;
      const years = Array.isArray(xLabels) && xLabels.length
        ? xLabels
        : (src[0]?.values || []).map(d => d.x);

      const width = el.clientWidth || 560;
      const isMobile = width < 480;
      const margin = { top: 16, right: 24, bottom: 36, left: isMobile ? 32 : 40 };
      const innerW = Math.max(0, width - margin.left - margin.right);
      const innerH = Math.max(0, height - margin.top - margin.bottom);

      const svg = d3
        .select(el)
        .append('svg')
        .attr('width', '100%')
        .attr('height', height)
        .attr('viewBox', `0 0 ${width} ${height}`);

      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

      // X scale (band for discrete years)
      // 첫 번째와 마지막 포인트가 가장자리에 더 가깝게 위치하도록 range 조정
      const x = d3
        .scalePoint()
        .domain(years)
        .range([-1, innerW + 1])
        .padding(0.2);

      // Y scale
      const allY = src.flatMap(s => s.values.map(v => Number(v.y) || 0));
      const yMax = Math.max(10, d3.max(allY) || 0);
      // Build Y scale. Note: d3.nice is a scale method, not a top-level function.
      // We first set the raw domain then call .nice with a reasonable tick count.
      const tickCount = yMax >= 50 ? 10 : 5;
      const y = d3
        .scaleLinear()
        .domain([0, yMax])
        .nice(tickCount)
        .range([innerH, 0]);

      // Grid (horizontal)
      g.append('g')
        .attr('class', 'grid')
        .call(d3.axisLeft(y).ticks(5).tickSize(-innerW).tickFormat(''))
        .call(gx => {
          gx.selectAll('line').attr('stroke', '#E4E8F1');
          gx.select('.domain').remove();
        });

      // Axes
      const fontSize = isMobile ? 11 : 12;
      g.append('g')
        .attr('transform', `translate(0,${innerH})`)
        .call(d3.axisBottom(x).tickSize(0))
        .call(gx => {
          gx.selectAll('text')
            .attr('fill', '#6C7687')
            .style('font-size', fontSize)
            .style('text-anchor', 'middle');
          gx.selectAll('.domain').remove();
        });

      g.append('g')
        .call(d3.axisLeft(y).ticks(5))
        .call(gx => {
          gx.selectAll('text').attr('fill', '#6C7687').style('font-size', fontSize);
          gx.selectAll('.domain').attr('stroke', '#E4E8F1');
        });

      // Line generator
      const line = d3
        .line()
        .x(d => x(d.x))
        .y(d => y(d.y))
        .curve(d3.curveMonotoneX);

      // Plot series
      const lineStrokeW = isMobile ? 1.5 : 2;
      src.forEach(s => {
        g.append('path')
          .datum(s.values)
          .attr('fill', 'none')
          .attr('stroke', s.color)
          .attr('stroke-width', lineStrokeW)
          .attr('d', line);

        // Points
        const pointR = isMobile ? 3 : 4;
        const strokeW = isMobile ? 1.5 : 2;
        g.selectAll(`circle.point-${s.key}`)
          .data(s.values)
          .enter()
          .append('circle')
          .attr('class', `point-${s.key}`)
          .attr('cx', d => x(d.x))
          .attr('cy', d => y(d.y))
          .attr('r', pointR)
          .attr('fill', '#fff')
          .attr('stroke', s.color)
          .attr('stroke-width', strokeW);
      });

      // Legend: SCIs, Scopus, KCI 중앙 정렬 + 모바일 반응형
      const legendCircleR = isMobile ? 5 : 6;
      const legendFontSize = isMobile ? 12 : 14;
      const legendGap = isMobile ? 12 : 18;
      const legendItemWidth = (s) => legendCircleR * 2 + 6 + Math.max(isMobile ? 36 : 48, s.label.length * (isMobile ? 6 : 8)) + legendGap;
      const totalLegendWidth = src.reduce((sum, s) => sum + legendItemWidth(s), 0) - legendGap;
      const legendStartX = margin.left + innerW / 2 - totalLegendWidth / 2;

      const legend = svg.append('g').attr('transform', `translate(${legendStartX},${height - 8})`);
      let lx = 0;
      src.forEach((s) => {
        const itemW = legendItemWidth(s);
        const gLeg = legend.append('g').attr('transform', `translate(${lx}, -6)`);
        gLeg
          .append('circle')
          .attr('cx', legendCircleR)
          .attr('cy', legendCircleR)
          .attr('r', legendCircleR)
          .attr('fill', s.color)
          .attr('opacity', 0.9);
        gLeg
          .append('text')
          .attr('x', legendCircleR * 2 + 6)
          .attr('y', legendCircleR + 3)
          .attr('fill', '#1F2937')
          .style('font-weight', 600)
          .style('font-size', legendFontSize)
          .style('text-anchor', 'start')
          .text(s.label);
        lx += itemW;
      });
    };

    draw();

    let ro;
    try {
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(() => draw());
        ro.observe(ref.current);
      } else {
        window.addEventListener('resize', draw);
      }
    } catch {
      window.addEventListener('resize', draw);
    }

    return () => {
      try {
        if (ro) ro.disconnect();
        else window.removeEventListener('resize', draw);
      } catch { }
    };
  }, [series, xLabels, height, externalSeries, outputLoading]);

  return (
    <div className="relative w-full min-h-[220px]">
      <div ref={ref} className="w-full" />
      {(externalSeries === null && outputLoading) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10 text-[#8D9AAF]">
          Loading...
        </div>
      )}
      {(externalSeries === null && outputErr) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10 text-[#F3652D] text-sm">
          {outputErr}
        </div>
      )}
    </div>
  );
}
